import { buildQuote } from "./utils.js";
/** Find the rate table entry for a given limit (exact match, else next higher). */
function lookupRate(limits, premiums, limitKes) {
    if (limitKes === 0)
        return 0;
    const idx = limits.findIndex((l) => l >= limitKes);
    return idx === -1 ? (premiums[premiums.length - 1] ?? 0) : (premiums[idx] ?? 0);
}
function findInpatientBand(age, bands) {
    return (bands.find((b) => age >= b.minAge && age <= b.maxAge) ?? bands[bands.length - 1]);
}
function findOutpatientBand(age, bands) {
    return (bands.find((b) => age >= b.minAge && age <= b.maxAge) ?? bands[bands.length - 1]);
}
function lookupInpatient(band, memberType, limits, limitKes) {
    return lookupRate(limits, band[memberType], limitKes);
}
export function priceIndividualMedical(input, rates) {
    const age = input.ageOfPrincipal;
    const hasSpouse = input.includeSpouse;
    const nChildren = input.numberOfChildren;
    // Total persons for per-person benefits (dental, optical, outpatient)
    const totalPersons = 1 + (hasSpouse ? 1 : 0) + nChildren;
    // ── Inpatient ───────────────────────────────────────────────────────────────
    const ipBand = findInpatientBand(age, rates.inpatient.bands);
    const ipLimits = rates.inpatient.limits;
    let inpatientKes = lookupInpatient(ipBand, "principal", ipLimits, input.inpatientLimitKes);
    if (hasSpouse) {
        inpatientKes += lookupInpatient(ipBand, "spouse", ipLimits, input.inpatientLimitKes);
    }
    for (let i = 0; i < nChildren; i++) {
        inpatientKes += lookupInpatient(ipBand, "child", ipLimits, input.inpatientLimitKes);
    }
    // ── Outpatient ──────────────────────────────────────────────────────────────
    // Principal and spouse use the principal's age band; children use the under-30 band.
    let outpatientKes = 0;
    if (input.outpatientLimitKes > 0) {
        const opBand = findOutpatientBand(age, rates.outpatient.bands);
        const opLimits = rates.outpatient.limits;
        const adultRate = lookupRate(opLimits, opBand.premiums, input.outpatientLimitKes);
        outpatientKes += adultRate; // principal
        if (hasSpouse)
            outpatientKes += adultRate; // spouse — same band as principal
        if (nChildren > 0) {
            // Children are always in the youngest outpatient band
            const childBand = findOutpatientBand(0, rates.outpatient.bands);
            const childRate = lookupRate(opLimits, childBand.premiums, input.outpatientLimitKes);
            outpatientKes += childRate * nChildren;
        }
    }
    // ── Maternity ───────────────────────────────────────────────────────────────
    const maternityKes = lookupRate(rates.maternity.limits, rates.maternity.premiums, input.maternityLimitKes);
    // ── Dental ──────────────────────────────────────────────────────────────────
    const dentalPerPerson = lookupRate(rates.dental.limits, rates.dental.premiums, input.dentalLimitKes);
    const dentalKes = dentalPerPerson * totalPersons;
    // ── Optical ─────────────────────────────────────────────────────────────────
    const opticalPerPerson = lookupRate(rates.optical.limits, rates.optical.premiums, input.opticalLimitKes);
    const opticalKes = opticalPerPerson * totalPersons;
    const items = [
        { code: "INPATIENT", label: "Inpatient Premium", amountKes: inpatientKes },
        { code: "OUTPATIENT", label: "Outpatient Premium", amountKes: outpatientKes },
        { code: "MATERNITY", label: "Maternity Premium", amountKes: maternityKes },
        { code: "DENTAL", label: "Dental Premium", amountKes: dentalKes },
        { code: "OPTICAL", label: "Optical Premium", amountKes: opticalKes },
    ];
    return buildQuote({
        product: "individual_medical",
        ratesVersion: rates.version,
        items,
        leviesKes: 0,
        stampDutyKes: rates.stampDutyKes,
    });
}
/** Total number of persons covered (principal + optional spouse + children). */
export function familySize(input) {
    return 1 + (input.includeSpouse ? 1 : 0) + input.numberOfChildren;
}
/** Compute family size without requiring full payload. */
export function computeFamilySize(input) {
    return 1 + (input.includeSpouse ? 1 : 0) + input.numberOfChildren;
}
